import { useEffect, useMemo } from 'react';

import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import Select from '@lotlinx-aws/sparky-component-library/Sparky/Select';
import SelectInput from '@lotlinx-aws/sparky-component-library/Sparky/SelectInput';
import { DateRangeKey, selectedDatePeriodAtom } from 'store/datePeriodAtom';

import styles from './DatePeriodSelect.module.scss';
import { vvoDatePeriodAtom } from 'pages/Inventory/VVO/VVOPage/VVO.atoms';
import classNames from 'classnames';

interface DatePeriodSelectProps {
  isVVODateRange: boolean;
  optionQuantity?: number;
  variant?: 'default' | 'plain';
  monthToDate?: boolean; // Make MTD actually MTD
  showPreviousMonths?: boolean;
  className?: string;
}

/**
 * Allows the user to select a date period from a dropdown.
 * Options include the current month and the previous three months.
 */
export const datePeriodOverrideAtom = atomWithStorage<DateRangeKey>('date-atom', '');

export default function DatePeriodSelect({
  isVVODateRange = false,
  optionQuantity = 3,
  variant = 'default',
  monthToDate = false,
  showPreviousMonths = false,
  className,
}: DatePeriodSelectProps) {
  const [selectedDatePeriod, setSelectedDatePeriod] = useAtom(selectedDatePeriodAtom);
  const [vvoDatePeriod, setVVODatePeriod] = useAtom(vvoDatePeriodAtom);
  const [datePeriodOverride, setPeriodOverride] = useAtom(datePeriodOverrideAtom);

  const options = useMemo(
    () => getDateRangeOptions(isVVODateRange, optionQuantity, monthToDate, showPreviousMonths),
    [isVVODateRange, optionQuantity, monthToDate, showPreviousMonths],
  );

  useEffect(() => {
    if (datePeriodOverride.length > 0 && !isVVODateRange) {
      setSelectedDatePeriod(datePeriodOverride as DateRangeKey);
      setPeriodOverride('');
    }
  }, [datePeriodOverride]);

  const handleSelectionChange = (key: string) => {
    if (isVVODateRange) {
      setVVODatePeriod(key as DateRangeKey);
    } else {
      setSelectedDatePeriod(key as DateRangeKey);
    }
  };

  return (
    <>
      {variant === 'plain' ? (
        <Select
          options={options}
          value={isVVODateRange ? (vvoDatePeriod as string) : (selectedDatePeriod as string)}
          onValueChange={handleSelectionChange}
          aria-label='Select Date Period'
          className={classNames([styles.dateSelectInput, className])}
        />
      ) : (
        <SelectInput
          id='Select Date Period'
          options={options}
          value={isVVODateRange ? (vvoDatePeriod as string) : (selectedDatePeriod as string)}
          onValueChange={handleSelectionChange}
          aria-label='Select Date Period'
          className={classNames([styles.dateSelectInput, className])}
          dropDownClassName={styles.dropDown}
        />
      )}
    </>
  );
}

/** Gets the date period options for the Select */
export function getDateRangeOptions(
  isVVODateRange: boolean,
  numPreviousMonths?: number,
  monthToDate?: boolean,
  lastThreeMonths?: boolean,
): { value: DateRangeKey; label: string }[] {
  if (isVVODateRange) {
    return [
      {
        value: 'Past 7 Days',
        label: 'Past 7 Days',
      },
      {
        value: 'Past 30 Days',
        label: 'Past 30 Days',
      },
      {
        value: 'Past 60 Days',
        label: 'Past 60 Days',
      },
      {
        value: 'Past 90 Days',
        label: 'Past 90 Days',
      },
      {
        value: 'MTD',
        label: 'Month to Date',
      },
    ];
  } else {
    const currentMonth = dayjs().format('MMM');
    // Current month – date range includes full month, despite label being "Month To Date"
    const options: { value: DateRangeKey; label: string }[] = [
      {
        value: 'MTD',
        label: `Current MTD ${lastThreeMonths ? `(${currentMonth})` : ''}`,
      },
    ];

    if (lastThreeMonths) {
      options.push({
        value: 'Last 2 Months',
        label: `Last 2 months (${dayjs().subtract(2, 'month').format('MMM')} - ${dayjs()
          .subtract(1, 'month')
          .format('MMM')})`,
      });
      options.push({
        value: 'Last 3 Months',
        label: `Last 3 months (${dayjs().subtract(3, 'month').format('MMM')} - ${dayjs()
          .subtract(1, 'month')
          .format('MMM')})`,
      });
    }
    // Get options for previous months (use YYYY-MM so keys are unique across years, e.g. last 24 months)
    for (let i = 1; i <= (numPreviousMonths ?? 3); i++) {
      const date = dayjs().subtract(i, 'month');
      options.push({
        value: date.format('YYYY-MM') as DateRangeKey,
        label: date.format('MMMM YYYY'),
      });
    }

    return options;
  }
}

/** DatePeriod is a tuple of [startDate, endDate] */
export type DatePeriod = [string, string];
/** Converts a DatePeriod to a string option key */
export function periodToKey(period: DatePeriod) {
  return period.join('-');
}

/** Converts an option key string back to a DatePerion */
export function keyToPeriod(key: string): DatePeriod {
  let split = key.split('-');
  return [split[0], split[1]];
}
